import { TableColumn } from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import LinkCell from '../Cells/LinkCell/LinkCell';
import ConnectionCell from '../Cells/ConnectionCell';
import DeviceLinkCell from '../Cells/LinkCell/DevicesLinkCall';
import BattaryCell from '../Cells/BattaryCell/BattaryCell';
import SnapTrapStatusCell from '../Cells/TrapStatusCell';
import LocationLinkCell from '../Cells/LinkCell/LocationLinkCall';
import { TrapTableTypeLink } from '../trapTables';

import ActionCell from 'components/TrapsTables/Cells/ActionCell/ActionCell';
import { Breakpoints } from 'hooks/Screen/useScreenSize';
import { SmartSnapTrapData, SnapTrapStatus } from 'api/traps/STTraps/types';

export const useGetSnapTrapColumns = (slug: string, type: TrapTableTypeLink): TableColumn<SmartSnapTrapData>[] => {
  const { t } = useTranslation();
  const columns: TableColumn<SmartSnapTrapData>[] = [];

  columns.push(
    {
      name: <i className="ri-wifi-fill fs-16" />,
      selector: row => row.connection_status,
      sortable: true,
      sortField: 'connection_status',
      cell: row => <ConnectionCell status={row.connection_status} />,
      width: '75px',
    },
    {
      name: <i className="ri-battery-charge-fill fs-17" />,
      selector: row => row.battery_percentage,
      sortable: true,
      sortField: 'battery_percentage',
      cell: row => <BattaryCell percentage={row.battery_percentage || 0} />,
      width: '75px',
      style: { justifyContent: 'center', height: '52px' },
    },
    {
      name: '1',
      selector: row => row.snap_1_status,
      sortable: true,
      sortField: 'snap_1_status',
      cell: row => (
        <SnapTrapStatusCell trapStatus={row.snap_1_status || SnapTrapStatus.DARK} time={row.time_from_last_activity} />
      ),
      width: '85px',
      style: { justifyContent: 'center' },
    },
  );

  type === 'smst' &&
    columns.push({
      name: '2',
      selector: row => row.snap_2_status,
      sortable: true,
      sortField: 'snap_2_status',
      cell: row => (
        <SnapTrapStatusCell trapStatus={row.snap_2_status || SnapTrapStatus.DARK} time={row.time_from_last_activity} />
      ),
      width: '85px',
      style: { justifyContent: 'center' },
    });

  columns.push(
    {
      name: (
        <div title={type === 'smst' ? t('TrapTables.SMSTNumberTooltip') : t('TrapTables.SRSTNumberTooltip')}>
          {t('TrapTables.Number')}
        </div>
      ),
      selector: row => row.sequence_number,
      sortable: true,
      sortField: 'sequence_number',
      cell: row => (
        <LinkCell
          title={row.sequence_number || 'None'}
          link={`/trap/${type}/info/${row.slug}`}
          toReact
          icon="ri-eye-line"
        />
      ),
      grow: 1,
    },
    {
      name: t('TrapTables.InteractiveMap'),
      selector: row => row.default_plan?.url_ending || '',
      cell: row => (
        <DeviceLinkCell
          data={row.default_plan ?? null}
          icon="ri-treasure-map-line"
          slug={slug}
          deviceType={type}
          device_add_slug={row.slug}
        />
      ),
      grow: 2,
      hide: Breakpoints.Tablet,
    },
    {
      name: t('TrapTables.Location'),
      selector: row => row?.location?.title || '-',
      sortable: true,
      sortField: 'location__title',
      cell: _row => _row?.location?.title || <LocationLinkCell data={null} icon="ri-map-pin-line" slug={slug} />,
      grow: 2,
      hide: Breakpoints.Tablet,
    },
    {
      name: t('TrapTables.LastConnection'),
      selector: row => (row?.last_connect ? dayjs(row.last_connect).format('YYYY-MM-DD\xa0\xa0\xa0HH:mm') : '-'),
      sortable: true,
      sortField: 'last_connect',
      hide: Breakpoints.Desktop,
    },
    {
      name: t('TrapTables.Action'),
      selector: row => row.default_plan?.on_plan || '',
      maxWidth: '90px',
      hide: Breakpoints.Mobile,
      cell: row => (
        <ActionCell
          qrCodeOptions={{ msg: `Device Serial Number: ${row.serial_number}`, qr_code: row.qr_code }}
          trap={{ pk: row.id, serialNumber: row.serial_number }}
          slug={slug}
        />
      ),
    },
  );

  return columns;
};
